import type { TypographySlot } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/look-types';
import getFontSize from '@loomstateorg/shared-frontend/utils/getFontSize';

/**
 * Brand-specific classes for the small content organisms (eco section, tiles, mini cards, footer columns,
 * category slider). Defaults are Cava. Each tastic passes the brand look to its organism.
 */
export type ContentLook = {
  ecoTitleTileClassName: string;
  ecoMiniCardClassName: string;
  ecoTextTileCtaClassName: string;
  tileCtaClassName: string;
  tilesGroupBgClassName: string;
  miniCardTitleRightClassName: string;
  footerColumnHeaderClassName: string;
  categoryTileLabelClassName: string;
  /** `Wrapper` background behind the category slider (brand `components/HOC/wrapper`). */
  categorySliderBackground?: 'neutral-200';
  /** Eco section title, computed from the CMS `titleFontSize` scale. */
  ecoTitleClassName: (titleFontSize: string) => string;
  /** Eco text tile: big number/title and the small label. */
  ecoTextTileTitleFontFamily: TypographySlot['fontFamily'];
  /** Extra classes on the eco text tile title (`text-lg` here overrides the `fontSize={28}` of the slot). */
  ecoTextTileTitleClassName: string;
  /** Content tile: title and CTA label fonts. */
  tileTitleFontFamily: TypographySlot['fontFamily'];
  tileCtaFontFamily: TypographySlot['fontFamily'];
  /** Hero: title font, CTA background per state and default CTA text color. */
  heroTitleFontFamily: TypographySlot['fontFamily'];
  heroTitleMarginClassName: string;
  heroButtonClassMap: {
    default: string;
    withHover: string;
    withBackground: string;
  };
  heroButtonTextClassName: string;
  /** Accordion button of FAQ-like sections (static-page / content-section). */
  faqAccordionButtonClassName: string;
  /** Wrapper around the eco card paragraph (mindfully-made); without it the paragraph is a spaced `span`. */
  ecoCardParagraphWrapperClassName?: string;
};

export const CAVA_CONTENT_LOOK: ContentLook = {
  ecoTitleTileClassName: 'mt-52 h-296 font-primaryBold text-52 md:h-[532px] lg:h-[668px]',
  ecoMiniCardClassName: 'bg-100 flex size-160 items-center space-x-4 bg-cover p-20 transition-shadow',
  ecoTextTileCtaClassName:
    'mt-10 rounded-md border-2 border-eco-light bg-white px-12 py-6 font-secondaryBold text-14 text-eco',
  tileCtaClassName: 'mt-24 bg-[#0E9C00]',
  tilesGroupBgClassName: '',
  miniCardTitleRightClassName: 'flex items-end space-x-10',
  footerColumnHeaderClassName: 'self-start sm:pb-20',
  categoryTileLabelClassName:
    'mt-5 block overflow-hidden truncate rounded-b-sm border-neutral-300 py-5 text-primary-regular md:text-16 lg:mt-0 lg:border lg:bg-white lg:py-10',
  ecoTitleClassName: (titleFontSize) => `text-center text-24 md:text-${getFontSize(18, titleFontSize)}`,
  ecoTextTileTitleFontFamily: 'primaryBold',
  ecoTextTileTitleClassName: 'text-lg font-bold',
  tileTitleFontFamily: 'primaryBold',
  tileCtaFontFamily: 'secondaryBold',
  heroTitleFontFamily: 'primaryBold',
  heroTitleMarginClassName: 'mt-22 md:mt-32',
  heroButtonClassMap: {
    default: 'bg-secondary-regular hover:bg-btn-primary-hover',
    withHover: 'bg-secondary-regular',
    withBackground: 'hover:bg-btn-primary-hover',
  },
  heroButtonTextClassName: 'text-neutral-150',
  faqAccordionButtonClassName: 'font-medium text-16 py-20 px-16 rounded-md',
};

/** Panda and Loom scale both breakpoints of the eco title from the CMS size. */
const scaledEcoTitleClassName = (titleFontSize: string) =>
  `text-center text-${getFontSize(24, titleFontSize)} md:text-${getFontSize(36, titleFontSize)}`;

export const PANDA_CONTENT_LOOK: ContentLook = {
  ...CAVA_CONTENT_LOOK,
  ecoTitleTileClassName: 'mt-52 h-296 font-NunitoSansBold text-52 md:h-[532px] lg:h-[668px]',
  ecoMiniCardClassName: 'bg-100 flex size-160 cursor-pointer items-center space-x-4 bg-cover p-20 transition-shadow',
  ecoTextTileCtaClassName:
    'mt-10 rounded-md border-2 border-eco-light bg-white px-12 py-6 font-PoppinsSemiBold text-14 text-eco',
  tileCtaClassName: 'mt-24 bg-eco',
  tilesGroupBgClassName: 'bg-neutral-200',
  miniCardTitleRightClassName: 'flex items-end space-x-2',
  footerColumnHeaderClassName: 'uppercase sm:pb-20',
  categorySliderBackground: 'neutral-200',
  ecoTitleClassName: scaledEcoTitleClassName,
  ecoTextTileTitleFontFamily: 'NunitoSansBold',
  // develop wrapped the slot in a `text-lg` span, so the slot's own text-28 won
  ecoTextTileTitleClassName: 'font-bold',
  tileTitleFontFamily: 'NunitoSansBold',
  tileCtaFontFamily: 'BarlowSemiCondensedSemiBold',
  heroTitleFontFamily: 'NunitoSansBold',
  heroButtonClassMap: {
    default: 'bg-secondary-regular hover:bg-secondary-regular hover:opacity-50',
    withHover: 'bg-secondary-regular',
    withBackground: 'hover:bg-secondary-regular hover:opacity-50',
  },
  faqAccordionButtonClassName: 'font-medium text-16 py-20 px-16 border-b rounded-md',
};

/** Chipotle forked Panda; the overrides reproduce what develop actually rendered (some dynamic classes there never applied). */
export const CHIPOTLE_CONTENT_LOOK: ContentLook = {
  ...PANDA_CONTENT_LOOK,
  ecoMiniCardClassName: CAVA_CONTENT_LOOK.ecoMiniCardClassName,
  ecoTitleClassName: (titleFontSize) => `md:text-${getFontSize(36, titleFontSize)}`,
  heroTitleMarginClassName: 'mt-22',
  ecoCardParagraphWrapperClassName: 'mt-15 text-lg',
};

export const LOOM_CONTENT_LOOK: ContentLook = {
  ...CAVA_CONTENT_LOOK,
  ecoTitleTileClassName: 'mt-52 h-296 font-secondaryBold text-52 md:h-[532px] lg:h-[668px]',
  ecoTextTileCtaClassName: 'mt-10 rounded-md border-2 border-eco-light px-12 py-6 font-secondaryBold text-14 text-eco',
  tilesGroupBgClassName: 'bg-neutral-200',
  categoryTileLabelClassName:
    'mt-5 block overflow-hidden truncate rounded-b-sm border-neutral-300 py-5 text-primary-regular md:text-16 lg:mt-0 lg:border lg:py-10',
  categorySliderBackground: 'neutral-200',
  ecoTitleClassName: scaledEcoTitleClassName,
  heroButtonClassMap: {
    default: 'border border-accent-color bg-accent-color text-neutral-150 hover:opacity-90 transition',
    withHover: 'border border-accent-color bg-accent-color text-neutral-150 hover:opacity-90 transition',
    withBackground: 'border border-accent-color hover:opacity-90 transition',
  },
  heroButtonTextClassName: 'text-primary-regular',
  faqAccordionButtonClassName: 'font-medium text-16 py-20 px-16 border-b rounded-md',
};
